import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Store,
  Package,
  Calendar,
  Shield,
  CheckCircle,
  AlertTriangle,
  MapPin,
  Tag,
  Clock,
  FileCheck,
  Truck
} from 'lucide-react';

export default function VendorCommitment() {
  const navigate = useNavigate();

  const commitments = [
    {
      title: 'The "Source-to-Door" Authenticity Guarantee',
      subtitle: 'Since customers are ordering specific regional delicacies (like Palani Prasadam or Agra Petha), the origin is everything.',
      icon: MapPin,
      color: 'text-green-500',
      bgColor: 'from-green-50 to-emerald-50',
      items: [
        {
          title: 'The Rule',
          description: 'Vendors must provide proof that items are sourced directly from the original authorised locations. No "generic" or "duplicate" versions are allowed. The brand name and origin must be clearly visible on the packaging.',
          isImportant: true
        }
      ]
    },
    {
      title: 'Mandatory "Freshness & Batch" Labeling',
      subtitle: 'Unlike a home-cooked meal, these items have a shelf life.',
      icon: Calendar,
      color: 'text-blue-500',
      bgColor: 'from-blue-50 to-indigo-50',
      items: [
        {
          title: 'The Rule',
          description: 'Every package must have a clear "Packed On" and "Best Before" date sticker. Vendors are prohibited from sending items that are within 25% of their expiry date, ensuring the customer has ample time to enjoy the delicacy.',
          isImportant: true
        }
      ]
    },
    {
      title: 'Transit-Proof Specialized Packaging',
      subtitle: 'Items like Petha (syrupy) or Prasadam (delicate) require more than just a paper bag.',
      icon: Package,
      color: 'text-orange-500',
      bgColor: 'from-orange-50 to-amber-50',
      items: [
        {
          title: 'The Rule',
          description: 'Vendors must use tamper-evident, leak-proof, and moisture-resistant packaging. If an item is crushed or leaks during transit due to poor primary packaging, the vendor must bear the full cost of the refund.',
          isImportant: true
        }
      ]
    },
    {
      title: 'Transparent Inventory & Lead Time',
      subtitle: 'These items aren\'t always available "on-demand" and may rely on stock arrivals.',
      icon: Clock,
      color: 'text-purple-500',
      bgColor: 'from-purple-50 to-violet-50',
      items: [
        {
          title: 'The Rule',
          description: 'Vendors must update stock levels daily. If an item is "In Transit" from the source, it must be marked as "Pre-order" with a clear delivery date, rather than letting a customer order an out-of-stock item.',
          isImportant: true
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/promise')}
              className="pl-0"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">Vendor's Commitment</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Store className="w-12 h-12 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
            Vendor's Commitment
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Our vendors bring regional specialties and authentic products to your doorstep. 
            These standards ensure quality and authenticity in every delivery.
          </p>
        </div>

        {/* Commitments */}
        <div className="space-y-8">
          {commitments.map((commitment, idx) => (
            <Card key={idx} className="overflow-hidden">
              <div className={`bg-gradient-to-r ${commitment.bgColor} p-6 border-b`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full bg-white shadow flex items-center justify-center ${commitment.color}`}>
                    <commitment.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">
                      {commitment.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {commitment.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {commitment.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex gap-3">
                      {item.isImportant ? (
                        <Shield className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <h4 className="font-semibold text-foreground">{item.title}</h4>
                        <p className={`text-sm ${item.isImportant ? 'text-foreground bg-green-50 p-3 rounded-lg mt-1 border border-green-100' : 'text-muted-foreground'}`}>
                          {item.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Badge Section */}
        <Card className="mt-12 border-2 border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-foreground mb-3">
              RuchiBox Verified Vendor
            </h3>
            <p className="text-muted-foreground max-w-lg mx-auto mb-6">
              Vendors who consistently meet these standards earn the prestigious 
              "RuchiBox Verified" badge, building customer trust and increasing orders.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                <FileCheck className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Authenticity Verified</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                <Truck className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Safe Packaging</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                <Tag className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Fresh Products</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
